import * as React from "react";

import { refetchCollaborateThreads } from "../api";
import { useCollaborateStore } from "../store/useCollaborateStore";
import type { CollaborateListView } from "../types/collaborateThread";
import CollabFilterChip from "./ui/CollabFilterChip";
import CollabVersionSelect from "./ui/CollabVersionSelect";

const PRIMARY_FILTERS: { id: CollaborateListView; label: string }[] = [
  { id: "comments", label: "All" },
  { id: "unread", label: "Unread" },
  { id: "mentions", label: "Mentions" },
  { id: "all-approvals", label: "Approvals" },
];

const APPROVAL_SUB_FILTERS: { id: CollaborateListView; label: string }[] = [
  { id: "all-approvals", label: "All approvals" },
  { id: "pending-mine", label: "Pending mine" },
  { id: "requested-by-me", label: "Requested by me" },
];

function isApprovalsView(view: CollaborateListView): boolean {
  return view === "all-approvals" || view === "pending-mine" || view === "requested-by-me";
}

export type CollaborateFilterRowProps = {
  documentId?: string;
};

const CollaborateFilterRow: React.FC<CollaborateFilterRowProps> = ({ documentId }) => {
  const listView = useCollaborateStore((s) => s.listView);
  const versionFilter = useCollaborateStore((s) => s.versionFilter);
  const listViewCounts = useCollaborateStore((s) => s.filterSummary.listViewCounts);
  const versionCounts = useCollaborateStore((s) => s.filterSummary.versionCounts);
  const setListView = useCollaborateStore((s) => s.setListView);
  const setVersionFilter = useCollaborateStore((s) => s.setVersionFilter);

  const approvalsOpen = isApprovalsView(listView);

  const handleListViewChange = (view: CollaborateListView) => {
    if (view === listView) {
      return;
    }
    setListView(view);
    void refetchCollaborateThreads(documentId);
  };

  const versionOptions = React.useMemo(
    () => [
      { value: "all", label: "All versions" },
      ...versionCounts.map(({ versionNumber }) => ({
        value: String(versionNumber),
        label: `v${versionNumber}`,
      })),
    ],
    [versionCounts],
  );

  return (
    <div className="collab-filters">
      <div className="collab-filters__primary" role="tablist" aria-label="Collaborate filters">
        {PRIMARY_FILTERS.map((item) => {
          const active =
            item.id === "all-approvals" ? approvalsOpen : listView === item.id;
          const count =
            item.id === "all-approvals"
              ? listViewCounts["all-approvals"] ?? 0
              : listViewCounts[item.id] ?? 0;
          return (
            <CollabFilterChip
              key={item.id}
              active={active}
              count={count}
              onClick={() => handleListViewChange(item.id)}
            >
              {item.label}
            </CollabFilterChip>
          );
        })}
        {versionCounts.length > 0 ? (
          <CollabVersionSelect
            value={versionFilter === null ? "all" : String(versionFilter)}
            options={versionOptions}
            aria-label="Filter by version"
            onChange={(value) => {
              const next = value === "all" ? null : Number.parseInt(String(value), 10);
              if (next === versionFilter || (next !== null && Number.isNaN(next))) {
                return;
              }
              setVersionFilter(next);
              void refetchCollaborateThreads(documentId);
            }}
          />
        ) : null}
      </div>
      {approvalsOpen ? (
        <div className="collab-filters__sub" role="tablist" aria-label="Approval filters">
          {APPROVAL_SUB_FILTERS.map((item) => (
            <CollabFilterChip
              key={item.id}
              active={listView === item.id}
              count={listViewCounts[item.id] ?? 0}
              onClick={() => handleListViewChange(item.id)}
            >
              {item.label}
            </CollabFilterChip>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default CollaborateFilterRow;
